/**
 * App.jsx
 *
 * Main application entry point for CapTrac React frontend.
 * Handles top-level routing, authentication context, and layout structure.
 *
 * - Uses React Router for navigation.
 * - Wraps all routes in AuthProvider for authentication state.
 * - Wraps protected content in AIServiceProvider for centralized AI configuration.
 * - Public routes: login, password reset, unauthorized, etc.
 * - Protected routes: require authentication and permissions, rendered within ProtectedLayout.
 *
 * Follows workspace guidelines for environment-aware, secure, and testable code.
 */

import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AIServiceProvider } from './context/AIServiceContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedLayout from './components/ProtectedLayout';
import GlobalCacheBanner from './components/GlobalCacheBanner';
import StagingWatermark from './components/StagingWatermark';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// General Purpose Pages
import LoginPage from './pages/LoginPages/LoginPage';
import RequestReset from './pages/LoginPages/RequestResetPage';
import ResetPassword from './pages/LoginPages/ResetPasswordPage';
import CreatePassword from './pages/UserPages/CreatePassword';
import HomePage from './pages/HomePages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Admin Pages
import UserProfile from './pages/UserPages/UserProfile';
import SettingsPage from './pages/SettingsPages/SettingsPage';
import CreateUser from './pages/UserPages/CreateUser';
import ViewUsers from './pages/UserPages/ViewUsers';
import UserPermissionsPage from './pages/PermissionPages/UserPermissionsPage';

// Dashboards

//Operations
import ExecutiveDashboard from './pages/Dashboards/ExecutiveDash/ExecutiveDashboard';
import OperationsDash from './pages/Dashboards/OperationsDash/OperationsDashboard';
import FootFallDashboard from './pages/Dashboards/FootFallDash/FootFallDashboard';
import SustainabilityDashboard from './pages/Dashboards/SustainDash/SustainabilityDashboard';

//Finance
import FinanceDashboard from './pages/Dashboards/FinanceDash/FinanceDashboard';
import ArrearsDashboard from './pages/Dashboards/ArrearsDash/ArrearsDashboard';

//Leasing
import LeasingDashboard from './pages/Dashboards/LeasingDash/LeasingDashboard';
//import AnchorTenantStats from './pages/LeasingPages/AnchorTenantStats';

//Legal
import LegalDashboard from './pages/Dashboards/LegalDash/LegalDashboard';
import OtherLegalMatters from './pages/LegalPages/OtherLegalMatters';

//Marketing
import MarketingDashboard from './pages/Dashboards/MarketingDash/MarketingDashboard';
import CsiDashboard from './pages/Dashboards/CSIDash/CsiDashboard';


//IT
import ITReport from './pages/Dashboards/IT/ITReport';
import ThreatHuntingReport from './pages/Dashboards/IT/ThreatHuntingReport';
import SOCOverviewDashboard from './pages/Dashboards/IT/SOCOverviewDashboard';
import NetworkSecurityDashboard from './pages/Dashboards/IT/NetworkSecurityDashboard';
import EndpointSecurityDashboard from './pages/Dashboards/IT/EndpointSecurityDashboard';
import RoutersOverviewPage from './pages/IT/RoutersOverviewPage';
import RouterDetailPage from './pages/IT/RouterDetailPage';
import RouterMonitoringSettingsPage from './pages/IT/RouterMonitoringSettingsPage';
import RouterProvisioningSettingsPage from './pages/IT/RouterProvisioningSettingsPage';
import RouterFirewallTemplatesPage from './pages/IT/RouterFirewallTemplatesPage';
import RouterUnifiedAddressListsPage from './pages/IT/RouterUnifiedAddressListsPage';
import ConnectivityMonitoringPage from './pages/IT/ConnectivityMonitoringPage';
import ITServiceDeskPage from './pages/IT/ITServiceDeskPage';
import KnowledgeBasePage from './pages/IT/KnowledgeBasePage';
import UnifiedEndpointManagementPage from './pages/IT/UnifiedEndpointManagementPage';


// Feature Pages
import FinanceConfigPage from './pages/SettingsPages/FinanceConfigPage';
import ScheduledTasksPage from './pages/SettingsPages/ScheduledTasksPage';
import EmailTemplatesPage from './pages/SettingsPages/EmailTemplatesPage';
import EmailMailboxesPage from './pages/SettingsPages/EmailMailboxesPage';
import ItTicketTypesPage from './pages/SettingsPages/ItTicketTypesPage';
import CsiProgrammesInputPage from './pages/MarketingPages/CsiProgrammesInputPage';
import CounterpartiesPage from './pages/MarketingPages/CounterpartiesPage';
import ContractsPage from './pages/MarketingPages/ContractsPage';
import MediaSpacesPage from './pages/MarketingPages/MediaSpacesPage';
import PromotionalAreasPage from './pages/MarketingPages/PromotionalAreasPage';
import MediaBookingsPage from './pages/MarketingPages/MediaBookingsPage';
import PromotionalBookingsPage from './pages/MarketingPages/PromotionalBookingsPage';
import NoChargeApprovalsPage from './pages/MarketingPages/NoChargeApprovalsPage';
import CMDailyChecklist from './pages/OperationsPages/CMDailyChecklist';
import NonComplianceForm from './pages/OperationsPages/NonComplianceForm';
import AssetManagementPage from './pages/OperationsPages/AssetManagementPage';
import IncidentListPage from './pages/OperationsPages/IncidentListPage';
import IncidentReportingPage from './pages/OperationsPages/IncidentReportingPage';
import AssetConfigPage from './pages/SettingsPages/AssetConfigPage';
import AssetServiceTypesConfigPage from './pages/SettingsPages/AssetServiceTypesConfigPage';
import AssetWorkflowRulesPage from './pages/SettingsPages/AssetWorkflowRulesPage';
import OutstandingCertificatesInputPage from './pages/OperationsPages/OutstandingCertificatesInputPage';
import OperationsManagementHubPage from './pages/OperationsPages/OperationsManagementHubPage';
import AssetImportExportPage from './pages/OperationsPages/AssetImportExportPage';

//Action Item Pages
import ActionItemsPage from './pages/ActionItems/ActionItemsPage';
import ActionItemDetailPage from './pages/ActionItems/ActionItemDetailPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import ProjectDetailPage from './pages/Projects/ProjectDetailPage';
import TemplateWizardPage from './pages/SettingsPages/TemplateWizardPage';

// New Page
import NotSupposedToSeePage from './pages/NotSupposedToSeePage';

//Non Dashboard Landing Pages
import CentreManagerHomePage from './pages/OperationsPages/CentreManagerPages.jsx/CentreManagerHomePage';

// SIPA Pages
import SipaDesignerPage from './pages/Sipa/SipaDesignerPage';
import SipaReportListPage from './pages/Sipa/SipaReportListPage';
import SipaReportCapturePage from './pages/Sipa/SipaReportCapturePage';
import SipaReportingPage from './pages/Sipa/SipaReportingPage';
import SipaWorkflowConfigPage from './pages/SettingsPages/SipaWorkflowConfigPage';

//Temporary Manual Data input Pages
import OpsStatsForm from './pages/DataInputsTemp/Operations/OpsStatsForm';
import OpsIncidentForm from './pages/DataInputsTemp/Operations/OpsIncidentForm';
import CsiProgrammesDataPage from './pages/MarketingPages/CsiProgrammesDataPage';

// Global notification service for action items
import ActionItemNotificationService from './components/ActionItemNotificationService';

// Property Management Pages
import ViewProperties from './pages/PropertyPages/ViewProperties';
import PropertyRoleMappingPage from './pages/PropertyPages/PropertyRoleMappingPage';
import PropertyGroupsPage from './pages/PropertyPages/PropertyGroupsPage';
import PropertyExternalMappingsPage from './pages/PropertyPages/PropertyExternalMappingsPage';
import BulkPropertyAssignment from './pages/PropertyPages/BulkPropertyAssignment';
import FloorPlanDesignerPage from './pages/PropertyPages/FloorPlanDesignerPage';

const App = () => {
    return (
        <AuthProvider>
            <AIServiceProvider>
                <GlobalCacheBanner />
                {/* Staging environment watermark - only visible on staging */}
                <StagingWatermark />
                {/* Global notification service for action item updates */}
                <ActionItemNotificationService />
                <Routes>
                    {/* Public routes (no authentication required) */}
                    <Route path="/" element={<LoginPage />} />
                    <Route path="/reset-password" element={<RequestReset />} />
                    <Route path="/reset-password/confirm" element={<ResetPassword />} />
                    <Route path="/create-password" element={<CreatePassword />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />

                    {/* Protected routes (require authentication, with layout/navbar) */}
                    <Route element={<ProtectedLayout />}>
                        {/*Main Routes*/}
                        <Route path="/home" element={<ProtectedRoute pageRoute="/home"><HomePage /></ProtectedRoute>} />
                        <Route path="/admin/user/profile" element={<ProtectedRoute pageRoute="/admin/user/profile"><UserProfile /></ProtectedRoute>} />
                        <Route path="/not-supposed-to-see" element={<ProtectedRoute pageRoute="/not-supposed-to-see"><NotSupposedToSeePage /></ProtectedRoute>} />
                        

                        {/*Administrative Page Routes*/}
                        <Route path="/admin/settings" element={<ProtectedRoute pageRoute="/admin/settings"><SettingsPage /></ProtectedRoute>} />
                        <Route path="/admin/users/create" element={<ProtectedRoute pageRoute="/admin/users/create"><CreateUser /></ProtectedRoute>} />
                        <Route path="/admin/users/view" element={<ProtectedRoute pageRoute="/admin/users/view"><ViewUsers /></ProtectedRoute>} />
                        <Route path="/admin/users/permissions" element={<ProtectedRoute pageRoute="/admin/users/permissions"><UserPermissionsPage /></ProtectedRoute>} />

                        {/*Operations Dashboards*/}
                        <Route path="/dashboards/executive-dash" element={<ProtectedRoute pageRoute="/dashboards/executive-dash"><ExecutiveDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/operations-dash" element={<ProtectedRoute pageRoute="/dashboards/operations-dash"><OperationsDash /></ProtectedRoute>} />
                        <Route path="/dashboards/footfall-dashboard" element={<ProtectedRoute pageRoute="/dashboards/footfall-dashboard"><FootFallDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/sustainability-dashboard" element={<ProtectedRoute pageRoute="/dashboards/sustainability-dashboard"><SustainabilityDashboard /></ProtectedRoute>} />

                        {/*Finance Dashboards*/}
                        <Route path="/dashboards/finance-dashboard" element={<ProtectedRoute pageRoute="/dashboards/finance-dashboard"><FinanceDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/arrears-dashboard" element={<ProtectedRoute pageRoute="/dashboards/arrears-dashboard"><ArrearsDashboard /></ProtectedRoute>} />

                        {/*Leasing Dashboards*/}
                        <Route path="/dashboards/leasing-dashboard" element={<ProtectedRoute pageRoute="/dashboards/leasing-dashboard"><LeasingDashboard /></ProtectedRoute>} />

                        {/*Legal Dashboards*/}
                        <Route path="/dashboards/legal-and-arrears-dashboard" element={<ProtectedRoute pageRoute="/dashboards/legal-and-arrears-dashboard"><LegalDashboard /></ProtectedRoute>} />

                        {/*Marketing Dashboards*/}
                        <Route path="/dashboards/media-and-promotions-dashboard" element={<ProtectedRoute pageRoute="/dashboards/media-and-promotions-dashboard"><MarketingDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/csi-dashboard" element={<ProtectedRoute pageRoute="/dashboards/csi-dashboard"><CsiDashboard /></ProtectedRoute>} />

                        {/*IT Pages*/}
                        <Route path="/dashboards/itreport" element={<ProtectedRoute pageRoute="/dashboards/itreport"><ITReport /></ProtectedRoute>} />
                        <Route path="/dashboards/threat-hunting" element={<ProtectedRoute pageRoute="/dashboards/threat-hunting"><ThreatHuntingReport /></ProtectedRoute>} />
                        <Route path="/dashboards/soc-overview" element={<ProtectedRoute pageRoute="/dashboards/soc-overview"><SOCOverviewDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/network-security" element={<ProtectedRoute pageRoute="/dashboards/network-security"><NetworkSecurityDashboard /></ProtectedRoute>} />
                        <Route path="/dashboards/endpoint-security" element={<ProtectedRoute pageRoute="/dashboards/endpoint-security"><EndpointSecurityDashboard /></ProtectedRoute>} />
                        <Route path="/it/config-management/routers" element={<ProtectedRoute pageRoute="/it/config-management/routers"><RoutersOverviewPage /></ProtectedRoute>} />
                        <Route path="/it/config-management/routers/:id" element={<ProtectedRoute pageRoute="/it/config-management/routers/:id"><RouterDetailPage /></ProtectedRoute>} />
                        <Route path="/it/config-management/router-monitoring-settings" element={<ProtectedRoute pageRoute="/it/config-management/router-monitoring-settings"><RouterMonitoringSettingsPage /></ProtectedRoute>} />
                        <Route path="/it/config-management/router-provisioning-settings" element={<ProtectedRoute pageRoute="/it/config-management/router-provisioning-settings"><RouterProvisioningSettingsPage /></ProtectedRoute>} />
                        <Route path="/it/config-management/router-firewall-templates" element={<ProtectedRoute pageRoute="/it/config-management/router-firewall-templates"><RouterFirewallTemplatesPage /></ProtectedRoute>} />
                        <Route path="/it/config-management/router-address-lists" element={<ProtectedRoute pageRoute="/it/config-management/router-address-lists"><RouterUnifiedAddressListsPage /></ProtectedRoute>} />
                        <Route path="/it/monitoring/connectivity" element={<ProtectedRoute pageRoute="/it/monitoring/connectivity"><ConnectivityMonitoringPage /></ProtectedRoute>} />
                        <Route path="/it/service-desk" element={<ProtectedRoute pageRoute="/it/service-desk"><ITServiceDeskPage /></ProtectedRoute>} />
                        <Route path="/it/knowledge-base" element={<ProtectedRoute pageRoute="/it/knowledge-base"><KnowledgeBasePage /></ProtectedRoute>} />
                        <Route path="/it/endpoint-management" element={<ProtectedRoute pageRoute="/it/endpoint-management"><UnifiedEndpointManagementPage /></ProtectedRoute>} />

                        {/*Action Items Pages*/}
                        <Route path="/admin/action-items" element={<ProtectedRoute pageRoute="/admin/action-items"><ActionItemsPage /></ProtectedRoute>} />
                        <Route path="/action-items/:id" element={<ProtectedRoute pageRoute="/action-items/:id"><ActionItemDetailPage /></ProtectedRoute>} />
                        <Route path="/settings/template-wizard" element={<ProtectedRoute pageRoute="/settings/template-wizard"><TemplateWizardPage /></ProtectedRoute>} />

                        {/*Projects Pages*/}
                        <Route path="/projects" element={<ProtectedRoute pageRoute="/projects"><ProjectsPage /></ProtectedRoute>} />
                        <Route path="/projects/:projectId" element={<ProtectedRoute pageRoute="/projects/:projectId"><ProjectDetailPage /></ProtectedRoute>} />


                        {/*SIPA Pages*/}
                        <Route path="/sipa/designer" element={<ProtectedRoute pageRoute="/sipa/designer"><SipaDesignerPage /></ProtectedRoute>} />
                        <Route path="/sipa/reports" element={<ProtectedRoute pageRoute="/sipa/reports"><SipaReportListPage /></ProtectedRoute>} />
                        <Route path="/sipa/report-capture" element={<ProtectedRoute pageRoute="/sipa/report-capture"><SipaReportCapturePage /></ProtectedRoute>} />
                        <Route path="/sipa/report-capture/:reportId" element={<ProtectedRoute pageRoute="/sipa/report-capture"><SipaReportCapturePage /></ProtectedRoute>} />
                        <Route path="/sipa/workflow-config" element={<ProtectedRoute pageRoute="/sipa/workflow-config"><SipaWorkflowConfigPage /></ProtectedRoute>} />
                        <Route path="/reporting/sipa-reporting" element={<ProtectedRoute pageRoute="/reporting/sipa-reporting"><SipaReportingPage /></ProtectedRoute>} />

                        {/*Centre Manager Daily Checklist Pages*/}
                        <Route path="/operations/cm-daily-checklist" element={<ProtectedRoute pageRoute="/operations/cm-daily-checklist"><CMDailyChecklist /></ProtectedRoute>} />

                        {/*Tenant Non-Compliance Pages*/}
                        <Route path="/operations/tenant-non-compliance-form" element={<ProtectedRoute pageRoute="/operations/tenant-non-compliance-form"><NonComplianceForm /></ProtectedRoute>} />

                        {/*Asset Management Pages*/}
                        <Route path="/operations/asset-management" element={<ProtectedRoute pageRoute="/operations/asset-management"><AssetManagementPage /></ProtectedRoute>} />
                        <Route path="/operations/asset-config" element={<ProtectedRoute pageRoute="/operations/asset-config"><AssetConfigPage /></ProtectedRoute>} />
                        <Route path="/settings/asset-service-types" element={<ProtectedRoute pageRoute="/settings/asset-service-types"><AssetServiceTypesConfigPage /></ProtectedRoute>} />
                        <Route path="/settings/asset-workflow-rules" element={<ProtectedRoute pageRoute="/settings/asset-workflow-rules"><AssetWorkflowRulesPage /></ProtectedRoute>} />
                        <Route path="/operations/asset-import-export" element={<ProtectedRoute pageRoute="/operations/asset-import-export"><AssetImportExportPage /></ProtectedRoute>} />

                        {/*Role Based Home Pages*/}
                        <Route path="/legal/home" element={<ProtectedRoute pageRoute="/legal/home"><LegalDashboard /></ProtectedRoute>} />
                        <Route path="/operations/centremanager/home" element={<ProtectedRoute pageRoute="/operations/centremanager/home"><CentreManagerHomePage /></ProtectedRoute>} />

                        {/*Temporary Data Input Pages*/}
                        <Route path="/settings/finance-config" element={<ProtectedRoute pageRoute="/settings/finance-config"><FinanceConfigPage /></ProtectedRoute>} />
                        <Route path="/settings/scheduled-tasks" element={<ProtectedRoute pageRoute="/settings/scheduled-tasks"><ScheduledTasksPage /></ProtectedRoute>} />
                        <Route path="/settings/email-templates" element={<ProtectedRoute pageRoute="/settings/email-templates"><EmailTemplatesPage /></ProtectedRoute>} />
                        <Route path="/settings/email-mailboxes" element={<ProtectedRoute pageRoute="/settings/email-mailboxes"><EmailMailboxesPage /></ProtectedRoute>} />
                        <Route path="/settings/it-ticket-types" element={<ProtectedRoute pageRoute="/settings/it-ticket-types"><ItTicketTypesPage /></ProtectedRoute>} />
                        <Route path="/legal/otherlegalmatters" element={<ProtectedRoute pageRoute="/legal/otherlegalmatters"><OtherLegalMatters /></ProtectedRoute>} />
                        <Route path="/marketing/csi-program-input" element={<ProtectedRoute pageRoute="/marketing/csi-program-input"><CsiProgrammesInputPage /></ProtectedRoute>} />
                        <Route path="/marketing/counterparties" element={<ProtectedRoute pageRoute="/marketing/counterparties"><CounterpartiesPage /></ProtectedRoute>} />
                        <Route path="/marketing/contracts" element={<ProtectedRoute pageRoute="/marketing/contracts"><ContractsPage /></ProtectedRoute>} />
                        <Route path="/marketing/media-spaces" element={<ProtectedRoute pageRoute="/marketing/media-spaces"><MediaSpacesPage /></ProtectedRoute>} />
                        <Route path="/marketing/promotional-areas" element={<ProtectedRoute pageRoute="/marketing/promotional-areas"><PromotionalAreasPage /></ProtectedRoute>} />
                        <Route path="/marketing/media-bookings" element={<ProtectedRoute pageRoute="/marketing/media-bookings"><MediaBookingsPage /></ProtectedRoute>} />
                        <Route path="/marketing/promotional-bookings" element={<ProtectedRoute pageRoute="/marketing/promotional-bookings"><PromotionalBookingsPage /></ProtectedRoute>} />
                        <Route path="/marketing/no-charge-approvals" element={<ProtectedRoute pageRoute="/marketing/no-charge-approvals"><NoChargeApprovalsPage /></ProtectedRoute>} />
                        <Route path="/settings/csi-project-data" element={<ProtectedRoute pageRoute="/settings/csi-project-data"><CsiProgrammesDataPage /></ProtectedRoute>} />
                        <Route path="/settings/operations-compliance" element={<ProtectedRoute pageRoute='/settings/operations-compliance'><OpsStatsForm /></ProtectedRoute>} />
                        <Route path="/settings/operations-incident" element={<ProtectedRoute pageRoute="/settings/operations-incident"><OpsIncidentForm /></ProtectedRoute>} />
                        <Route path="/operations/incidents" element={<ProtectedRoute pageRoute="/operations/incidents"><IncidentReportingPage /></ProtectedRoute>} />
                        <Route path="/operations/outstanding-certificates" element={<ProtectedRoute pageRoute="/operations/outstanding-certificates"><OutstandingCertificatesInputPage /></ProtectedRoute>} />
                        <Route path="/operations/management-hub" element={<ProtectedRoute pageRoute="/operations/management-hub"><OperationsManagementHubPage /></ProtectedRoute>} />

                        {/*Property Management Pages*/}
                        <Route path="/admin/view-properties" element={<ProtectedRoute pageRoute="/admin/view-properties"><ViewProperties /></ProtectedRoute>} />
                        <Route path="/admin/property-role-settings" element={<ProtectedRoute pageRoute="/admin/property-role-settings"><PropertyRoleMappingPage /></ProtectedRoute>} />
                        <Route path="/admin/property-groups" element={<ProtectedRoute pageRoute="/admin/property-groups"><PropertyGroupsPage /></ProtectedRoute>} />
                        <Route path="/admin/property-external-mappings" element={<ProtectedRoute pageRoute="/admin/property-external-mappings"><PropertyExternalMappingsPage /></ProtectedRoute>} />
                        <Route path="/admin/bulk-property-assignment" element={<ProtectedRoute pageRoute="/admin/bulk-property-assignment"><BulkPropertyAssignment /></ProtectedRoute>} />
                        <Route path="/property/:propertyId/floorplan" element={<ProtectedRoute pageRoute="/property/:propertyId/floorplan"><FloorPlanDesignerPage /></ProtectedRoute>} />

                    </Route>

                    {/* Catch-all: 404 Not Found */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
                {/* Global toast container for notifications across all pages */}
                <ToastContainer 
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </AIServiceProvider>
        </AuthProvider>
    );
};

export default App;
